import { notification } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UseMutateFunction, useMutation, useQueryClient } from 'react-query'

import { QUERY_KEYS } from '@api'
import { AxiosErrorType, RailcarUpdatePayload } from '@types'

import RailcarApi from './api'

const { put } = RailcarApi

interface UseUpdateRailcarProps {
  id: string | number
}
interface UseUpdateRailcarReturn {
  updateRailcar: UseMutateFunction<
    RailcarUpdatePayload,
    AxiosErrorType,
    RailcarUpdatePayload,
    unknown
  >
  updateRailcarIsFetching?: boolean
}

const useUpdateRailcar = ({ id }: UseUpdateRailcarProps): UseUpdateRailcarReturn => {
  const { t } = useTranslation()
  const [updateRailcarIsFetching, setUpdateRailcarIsFetching] = useState<boolean>()

  const queryClient = useQueryClient()

  const onSuccess = () => {
    notification.success({
      message: t('edit_railcar_succesfully'),
    })
    queryClient.invalidateQueries(QUERY_KEYS.BLOCKTRAINS)
    queryClient.invalidateQueries(QUERY_KEYS.BLOCKTRAIN)
    setUpdateRailcarIsFetching(false)
  }

  const onError = (err: AxiosErrorType) => {
    setUpdateRailcarIsFetching(false)
    if (err.status === 404) {
      console.error('Route not found', { err })
    }
    const errorResponse = err.data.error as {
      type: string
      status: number
      message: string
    }

    if (errorResponse.message) {
      notification.error({
        message: t(errorResponse.message),
      })
    } else {
      notification.error({
        message: t('cannot_update_railcar'),
      })
    }
  }

  const { mutate: updateRailcar } = useMutation<
    RailcarUpdatePayload,
    AxiosErrorType,
    RailcarUpdatePayload
  >(payload => put(id!, payload), {
    onSuccess,
    onError,
    onMutate: () => {
      setUpdateRailcarIsFetching(true)
    },
  })

  return {
    updateRailcar,
    updateRailcarIsFetching,
  }
}

export { useUpdateRailcar }
